import { useState } from "react";
import { Link } from "react-router-dom";

const values = [
  {
    icon: "ri-heart-2-line",
    title: "内容为先",
    text: "我们只关心一件事：有没有让你想一直读下去的好故事。",
  },
  {
    icon: "ri-shield-check-line",
    title: "干净阅读",
    text: "没有干扰性的弹窗与跳转，把注意力还给作品本身。",
  },
  {
    icon: "ri-plant-line",
    title: "扶持创作",
    text: "通过扶持计划与保底稿费，让新人作者敢把故事讲完。",
  },
  {
    icon: "ri-community-line",
    title: "陪伴追更",
    text: "更新提醒与清晰目录，让你的每次追更都不落空。",
  },
];

export default function AboutPage() {
  const [copied, setCopied] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      window.setTimeout(() => setCopied(""), 2000);
    } catch {
      setCopied("");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") params.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch("https://readdy.ai/api/form/db91mm8kk00l6eltro20", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const data = parsed as
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null;
      const serverMsg =
        data?.meta?.message || data?.message || data?.meta?.detail || "";
      const detail = data?.meta?.detail || "";
      const isSpam = `${serverMsg} ${detail}`.toLowerCase().includes("spam");

      if (response.ok && data?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || detail || "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <div className="w-full">
      <section className="relative w-full pt-24 md:pt-28 pb-16 bg-foreground-950 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20background%20of%20soft%20green%20leaf%20shapes%20and%20flowing%20lines%20with%20warm%20golden%20light%20accents%2C%20minimal%20modern%20illustration%2C%20smooth%20gradients%2C%20calm%20and%20airy%20composition%2C%20no%20text&width=1600&height=700&seq=waman-about-hero&orientation=landscape"
            alt="蛙漫品牌抽象背景"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 to-foreground-950/70"></div>
        <div className="relative z-10 w-full px-4 md:px-6 text-background-50">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/80 text-xs font-label font-bold">
            <i className="ri-leaf-line"></i>
            关于蛙漫
          </span>
          <h1 className="mt-5 text-3xl md:text-5xl font-label font-bold leading-tight max-w-3xl">
            我们想让每一只「追更的青蛙」，
            <br className="hidden md:block" />
            都能安静地读完一个好故事
          </h1>
          <p className="mt-5 max-w-2xl text-sm md:text-base text-background-100/90 leading-relaxed">
            蛙漫创立于 2024 年，是一个面向漫画与动漫爱好者的在线阅读平台。我们相信，阅读本身应该是舒服的、不被打扰的，所以从页面布局到章节目录，每一个细节都围绕「让你更快进入故事」来设计。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="lg:w-1/2">
            <h2 className="text-xl md:text-2xl font-label font-bold text-foreground-950">
              我们的服务宗旨
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              蛙漫提供涵盖热血、恋爱、玄幻、科幻、悬疑、治愈、校园与古风等题材的漫画作品，支持在线浏览章节、查看更新动态、筛选感兴趣的分类，并同步更新最新动漫资讯。我们希望无论你是想在通勤路上补完一话，还是想认真追一部刚开更的新作，都能在这里找到属于自己的角落。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              本页面为演示项目，所有漫画、作者与资讯内容均为虚构，用于展示平台的阅读与资讯浏览体验。我们不提供支付、下载与账号登录功能，收藏仅保存在你的浏览器本地。
            </p>
          </div>

          <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {values.map((item) => (
              <div
                key={item.title}
                className="p-5 rounded-lg bg-background-100 border border-background-200"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 mb-3">
                  <i className={`${item.icon} text-lg text-primary-700`}></i>
                </span>
                <h3 className="text-base font-label font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-14 md:pb-20">
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="lg:w-[42%]">
            <h2 className="text-xl md:text-2xl font-label font-bold text-foreground-950">
              联系我们
            </h2>
            <p className="mt-3 text-sm text-foreground-600">
              有任何建议、内容合作或版权相关的问题，欢迎通过以下方式找到我们。
            </p>

            <div className="mt-6 flex flex-col gap-3">
              {[
                { icon: "ri-mail-line", label: "合作邮箱", value: "hello@waman-comic.com" },
                { icon: "ri-phone-line", label: "客服电话", value: "400-800-2026" },
                { icon: "ri-map-pin-line", label: "办公地址", value: "滨江会展中心 3 号馆" },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => copyText(item.value, item.label)}
                  className="flex items-center justify-between gap-3 px-4 py-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors text-left cursor-pointer"
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-50">
                      <i className={`${item.icon} text-primary-600`}></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-foreground-500">
                        {item.label}
                      </span>
                      <span className="block text-sm font-label font-bold text-foreground-900 truncate">
                        {item.value}
                      </span>
                    </span>
                  </span>
                  <span className="text-xs font-label text-primary-600 whitespace-nowrap">
                    {copied === item.label ? "已复制" : "点击复制"}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-6 p-4 rounded-lg bg-accent-50 border border-accent-200">
              <p className="text-sm text-accent-900 flex items-start gap-2">
                <i className="ri-information-line mt-0.5"></i>
                感谢每一份认真的反馈，我们会逐条阅读，并在后续版本中持续优化阅读体验。
              </p>
            </div>
          </div>

          <div className="lg:flex-1">
            <div className="p-5 md:p-7 rounded-lg bg-background-100 border border-background-200">
              <h2 className="text-xl md:text-2xl font-label font-bold text-foreground-950">
                意见反馈
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                填写下面的表单，把你的想法告诉我们。
              </p>

              <form
                id="waman-feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-4"
              >
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 flex flex-col gap-1.5">
                    <label htmlFor="fb-name" className="text-sm font-label font-bold text-foreground-800">
                      昵称
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你？"
                      className="h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-1.5">
                    <label htmlFor="fb-email" className="text-sm font-label font-bold text-foreground-800">
                      邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="fb-type" className="text-sm font-label font-bold text-foreground-800">
                    反馈类型
                  </label>
                  <select
                    id="fb-type"
                    name="feedback_type"
                    defaultValue="功能建议"
                    className="h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                  >
                    <option value="功能建议">功能建议</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="版权相关">版权相关</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="其他反馈">其他反馈</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="fb-message" className="text-sm font-label font-bold text-foreground-800">
                    反馈内容
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你遇到的问题或建议（最多 500 字）"
                    className="px-3 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>

                <div className="contact-extra-field" aria-hidden="true">
                  <label htmlFor="fb-website-alt">Company website</label>
                  <input
                    id="fb-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer"
                >
                  <i className="ri-send-plane-line"></i>
                  {status === "submitting" ? "提交中..." : "提交反馈"}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-2 text-sm text-primary-700">
                    <i className="ri-checkbox-circle-line"></i>
                    提交成功，感谢你的反馈！
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 text-sm text-secondary-700">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>

            <div className="mt-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-300 text-foreground-800 text-sm font-label font-bold hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}